import { KeyManager } from 'jasper-roche-crypto';
import actions, { SEND_ADELANTO } from './adelanto.actions';
import contractActions from '../contract/contract.actions';
import services from './adelanto.services';
import signTransaction from '../utils/signTransaction';

let adelantoSendInFlight = false;

export const __resetAdelantoSendInFlightForTests = () => {
    adelantoSendInFlight = false;
};

const getAdelantoErrorDetail = (err) => {
    const data = err?.response?.data ?? err?.data;
    if (!data) return err?.message || String(err);
    if (typeof data === 'string') return data;
    if (data.description) return String(data.description);
    if (data.detail) return String(data.detail);
    if (data.message) return String(data.message);
    if (data.error) {
        return data.description ? String(data.description) : String(data.error);
    }
    if (typeof data === 'object') {
        const parts = Object.entries(data).map(([field, msgs]) => {
            const text = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
            return `${field}: ${text}`;
        });
        if (parts.length) return parts.join('; ');
    }
    return err?.message || String(err);
};

const isNonceDesyncError = (err) => /nonce desincronizado/i.test(getAdelantoErrorDetail(err));

const mapAdelantoSendError = (err) => {
    const detail = getAdelantoErrorDetail(err);
    if (/Invalid URL 'None'|nodo RPC/i.test(detail)) {
        return 'El backend de testnet no tiene RPC configurado (RPC_URL). Contactá al equipo de backend.';
    }
    if (isNonceDesyncError(err)) {
        return 'El nonce cambió durante el envío. Reintentá.';
    }
    return detail;
};

const normalizeAdelantoSendError = (err) => ({
    ...err,
    message: mapAdelantoSendError(err),
    data: { detail: [mapAdelantoSendError(err)] },
});

const validateIniciarAdelantoResponse = (response) => {
    if (!response?.transactionDetails) {
        throw new Error('iniciarpago no devolvió transactionDetails.');
    }
    const executionId = response.executionId ?? response.execution_id;
    if (executionId == null) {
        throw new Error('iniciarpago no devolvió executionId.');
    }
    return {
        executionId,
        transactionDetails: response.transactionDetails,
    };
};

const adelantoMiddleware =
    ({ dispatch, getState }) =>
    (next) =>
    (action) => {
        next(action);
        switch (action.type) {
            case SEND_ADELANTO: {
                if (adelantoSendInFlight) {
                    dispatch(
                        actions.sendAdelantoError(
                            normalizeAdelantoSendError({
                                message: 'Ya hay un envío de NEX en curso.',
                            }),
                        ),
                    );
                    break;
                }

                const { versionId, contractId, producto, observacion } = action;

                if (!versionId) {
                    dispatch(
                        actions.sendAdelantoError({
                            data: { detail: ['versionId requerido'] },
                        }),
                    );
                    break;
                }

                if (!producto) {
                    dispatch(
                        actions.sendAdelantoError({
                            data: { detail: ['producto requerido para enviar NEX'] },
                        }),
                    );
                    break;
                }

                const username = getState()?.profile?.user?.username;
                if (!username) {
                    dispatch(
                        actions.sendAdelantoError({
                            data: { detail: ['Usuario no autenticado'] },
                        }),
                    );
                    break;
                }

                adelantoSendInFlight = true;
                const fundsKey = new KeyManager(username).deriveFundsKey();

                const finishSend = (res) => {
                    dispatch(actions.sendAdelantoResponse(res));
                    if (contractId) {
                        dispatch(contractActions.getContract(contractId));
                    }
                    if (action.successCallback) action.successCallback(res);
                };

                const runSend = async () => {
                    try {
                        const iniciarRes = await services.iniciarAdelantoPago({
                            contrato_version: versionId,
                            producto,
                            observacion: observacion ?? undefined,
                        });

                        const { executionId, transactionDetails } =
                            validateIniciarAdelantoResponse(iniciarRes);
                        const signedTransaction = await signTransaction(
                            transactionDetails,
                            fundsKey,
                        );
                        const broadcastRes = await services.broadcastAdelantoPago({
                            executionId,
                            signedTransaction,
                            transactionDetails,
                        });

                        finishSend(broadcastRes);
                    } catch (err) {
                        dispatch(actions.sendAdelantoError(normalizeAdelantoSendError(err)));
                    } finally {
                        adelantoSendInFlight = false;
                    }
                };

                runSend();
                break;
            }
            default:
                break;
        }
    };

export default adelantoMiddleware;
